import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import store from './store';
import { logEvent } from './logger';
import { getIdToken } from '../apis/firebase';
import { LEVELS, SEVERITY, API_GET_IP_ADDRESS } from '../constants';

let globalIpAddress: string | null = null;
let ipPromise: Promise<string | null> | null = null;

const fetchGlobalIpAddress = async (): Promise<string | null> => {
    if (globalIpAddress) return globalIpAddress;
    if (ipPromise) return ipPromise;

    ipPromise = (async () => {
        try {
            const response = await axios.get(API_GET_IP_ADDRESS);
            globalIpAddress = response.data.ip;
            return globalIpAddress;
        } catch (err) {
            logEvent({
                level: LEVELS.ERROR,
                message: 'Impossible de récupérer l’adresse IP globale',
                severity: SEVERITY.LOW,
                contexte: {
                    apiEndpoint: API_GET_IP_ADDRESS,
                    error: err instanceof Error ? err.message : JSON.stringify(err),
                },
            });

            return null;
        } finally {
            ipPromise = null;
        }
    })();

    return ipPromise;
};


interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    addCompanyId?: boolean;
    logMessage?: any;
    functionDetails?: Record<string, unknown>;
    severity?: string;
    _retry403?: boolean;
}

const apiService = axios.create();


apiService.interceptors.request.use(async (config: CustomAxiosRequestConfig) => {
    const freshToken = await getIdToken();
    if (freshToken && !config.headers?.Authorization) {
        config.headers = {
            ...config.headers,
            Authorization: `Bearer ${freshToken}`,
        };
    }
    const { companyId } = store.getState().user || {};
    if (companyId && config.addCompanyId) {
        if (config.method === 'get') {
            config.params = { ...config.params, companyId };
        } else {
            config.data = { ...config.data, companyId };
        }
    }

    return config;
});

apiService.interceptors.response.use(
    async (response: AxiosResponse) => {
        const config = response.config as CustomAxiosRequestConfig;

        const ip = globalIpAddress || (await fetchGlobalIpAddress());
        const defaulInfoMessage = 'Opération réussie'


        logEvent({
            level: LEVELS.INFO,
            message: config.logMessage || defaulInfoMessage,
            contexte: {
                apiEndpoint: response.config.url,
                ipAddress: ip,
            },
        });
        return response;
    },
    async (error: AxiosError) => {
        const config = error.config as CustomAxiosRequestConfig;
        const response = error.response;
        if (response?.status === 403 && config && !config._retry403) {
            config._retry403 = true;
            try {
                const freshToken = await getIdToken(undefined, true);
                if (freshToken) {
                    config.headers = {
                        ...(config.headers || {}),
                        Authorization: `Bearer ${freshToken}`,
                    };
                }
                return apiService(config);
            } catch (e) {
                // log erreur refresh
                logEvent({
                    level: LEVELS.ERROR,
                    message: 'Échec du rafraîchissement du token après 403',
                    severity: SEVERITY.HIGH,
                    errorCode: response?.status,
                    contexte: { apiEndpoint: config?.url },
                    fonction: config?.functionDetails || {},
                });
                return Promise.reject(error);
            }
        }

 
  
        const ip = globalIpAddress || (await fetchGlobalIpAddress());
        const functionDetails = (error.config as CustomAxiosRequestConfig).functionDetails || {};
        const severity = (error.config as CustomAxiosRequestConfig).severity
        let errorMessage;
        if (Array.isArray(response?.data?.errorMessage)) {
            errorMessage = response?.data.errorMessage[0]?.message || 'Unknown error';
        } else if (typeof response?.data?.errorMessage === 'string') {
            errorMessage = response?.data.errorMessage;
        } else {
            errorMessage = JSON.stringify(response?.data?.errorMessage, null, 2);
        }

        logEvent({
            level: LEVELS.ERROR,
            message: errorMessage,
            severity: severity || SEVERITY.MEDIUM,
            errorCode: response?.status,
            contexte: {

                apiEndpoint: error.config?.url,
                ipAddress: ip,

            },
            fonction: {
                ...functionDetails,
            },
        });

        throw new Error(errorMessage);
    }
);


export const sendGetRequest = (
    url: string,
    data: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    addCompanyIdToBody = true,
    functionDetails?: Record<string, unknown>,
    logMessage?: any
) => {
    const { companyId } = store.getState().user;
    const newData = addCompanyIdToBody
        ? { ...data, companyId }
        : data;
    return apiService({
        method: 'get',
        url,
        params: newData,
        headers,
        addCompanyId: addCompanyIdToBody,
        functionDetails,
        logMessage,
    } as CustomAxiosRequestConfig);
};

export const sendPostRequest = (
    url: string,
    data: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    compId: string | null = null,
    addCompanyIdToBody = true,
    functionDetails?: Record<string, unknown>,
    logMessage?: any,
    severity?: string
) => {
    let companyId = '';
    if (addCompanyIdToBody) {
        if (!compId) {
            companyId = store.getState().user.companyId;
        } else {
            companyId = compId;
        }
    }
    const newData = addCompanyIdToBody
        ? { ...data, companyId }
        : data;
    if (addCompanyIdToBody && compId === '') {
        delete newData.companyId;
    }

    return apiService({
        method: 'post',
        url,
        data: newData,
        headers,
        addCompanyId: addCompanyIdToBody && !compId,
        functionDetails,
        logMessage,
        severity,
    } as CustomAxiosRequestConfig);
};

export const sendPutRequest = (
    url: string,
    data: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    compId: string | null = null,
    addCompanyIdToBody = true,
    functionDetails?: Record<string, unknown>,
    logMessage?: any,
    severity?: string
) => {
    let companyId = '';
    if (!compId) {
        companyId = store.getState().user.companyId;
    } else {
        companyId = compId;
    }
    const newData = addCompanyIdToBody ? { ...data, companyId } : data;
    if (addCompanyIdToBody && compId === '') {
        delete newData.companyId;
    }

    return apiService({
        method: 'put',
        url,
        data: newData,
        headers,
        addCompanyId: addCompanyIdToBody,
        functionDetails,
        logMessage,
        severity,
    } as CustomAxiosRequestConfig);
};

export const sendDeleteRequest = (
    url: string,
    data: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    addCompanyIdToBody = true,
    functionDetails?: Record<string, unknown>,
    logMessage?: any
) => {
    const { companyId } = store.getState().user;
    const newData = { ...data, companyId };
    return apiService({
        method: 'delete',
        url,
        data: newData,
        headers,
        addCompanyId: addCompanyIdToBody,
        functionDetails,
        logMessage,
    } as CustomAxiosRequestConfig);
};


